export default {
  namespaced: true,

  getters: {
    some(state, getters, rootState, rootGetters) {
      const config = rootGetters['builder/config'];
      return (
        config &&
        config.entities &&
        config.entities.properties &&
        Object.keys(config.entities.properties).length
      );
    },

    keys(state, getters, rootState, rootGetters) {
      if (!getters.some) {
        return [];
      }

      const config = rootGetters['builder/config'];
      return Object.keys(config.entities.properties);
    },

    property(state, getters, rootState, rootGetters) {
      return (key) => {
        if (!getters.some) {
          throw new Error(`Invalid property ${key}`);
        }

        const property =
          rootGetters['builder/config'].entities.properties[
            key
          ];

        if (!property) {
          throw new Error(`Invalid property ${key}`);
        }

        return property;
      };
    },

    enabled(state, getters, rootState, rootGetters) {
      return (key) => {
        if (!key) {
          return getters.some;
        }

        const property = getters.property(key);

        return rootGetters['builder/matcher/matches'](
          property.condition,
        );
      };
    },

    required(state, getters) {
      return (key) => {
        const property = getters.property(key);
        return !!property.required;
      };
    },

    input(state, getters) {
      return (key) => {
        const { input } = getters.property(key);

        if (!input) {
          return 'text';
        }

        return input.toLowerCase();
      };
    },

    label(state, getters) {
      return (key) => {
        const property = getters.property(key);
        return property.label;
      };
    },

    htmlField(state, getters) {
      return (key) => {
        const input = getters.input(key);

        if (!input) {
          return 'text';
        }

        if (
          [
            'select',
            'entity',
            'field',
            'field_to_display',
          ].includes(input)
        ) {
          return 'select';
        }

        return input;
      };
    },

    help(state, getters) {
      return (key) => {
        const property = getters.property(key);

        if (!property.help) {
          return null;
        }

        return property.help;
      };
    },

    defaults(state, getters) {
      const { keys } = getters;

      if (!keys.length) {
        return {};
      }

      const defaults = {};

      keys.forEach((key) => {
        const defaultValue = getters.defaultValue(key);

        if (!defaultValue && defaultValue !== false) {
          return;
        }

        defaults[key] = defaultValue;
      });

      return defaults;
    },

    defaultValue(state, getters) {
      return (key) => {
        const property = getters.property(key);
        return property.default;
      };
    },

    choices(state, getters) {
      return (entities, entity, key) => {
        const input = getters.input(key);

        if (input === 'entity') {
          return getters.entityChoices(entities, entity);
        }

        if (input === 'field') {
          return getters.fieldChoices(entity);
        }

        if (input === 'field_to_display') {
          return getters.fieldChoicesToDisplay(entity);
        }

        return getters.propertyChoices(key);
      };
    },

    entityChoices() {
      return (entities, entity) => {
        const choices = [
          {
            value: entity.id,
            label:
              entity.name && entity.name.singular
                ? entity.name.singular
                : 'this',
          },
        ];

        if (!entities || !entities.length) {
          return choices;
        }

        return [
          ...choices,
          ...entities
            .filter(
              (filteredEntity) =>
                filteredEntity.id !== entity.id,
            )
            .map((mappedEntity) => ({
              value: mappedEntity.id,
              label: mappedEntity.name.singular,
            })),
        ];
      };
    },

    fieldChoices() {
      return ({ fields }) => {
        if (!fields || !fields.length) {
          return [];
        }

        return fields.map((field) => ({
          value: field.id,
          label: field.name.singular,
        }));
      };
    },

    fieldChoicesToDisplay() {
      return ({ fields }) => {
        const output = [
          {
            value: 'id',
            label: 'id',
          },
        ];

        if (!fields || !fields.length) {
          return output;
        }

        return [
          ...output,
          ...fields
            .filter((field) => {
              return [
                'text',
                'integer',
                'decimal',
                'enumerator',
              ].includes(field.type);
            })
            .map((field) => ({
              value: field.id,
              label: field.name.singular,
            })),
        ];
      };
    },

    propertyChoices(state, getters) {
      return (key) => {
        const { choices } = getters.property(key);

        if (!choices || !Object.keys(choices).length) {
          return [];
        }

        return Object.keys(choices).map((choiceKey) => ({
          value: choiceKey,
          label: choices[choiceKey].label,
        }));
      };
    },
  },
};
